import { setDB } from 'db'
import {
    createEffect,
    createRoot,
    createSignal,
    createUniqueId,
    JSX,
    onMount,
} from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import { USER_THEME, USER_WELCOMED } from './dbLabels'
import {
    AgentInfo,
    api_user_get,
    api_user_inventory_get,
    api_user_logout_post,
    api_user_saved_raw_get,
    EateryCard,
    EateryStats,
    Gene,
    SeedError,
    UserGender,
    UserImpression,
    UserInfo,
    UserInventoryInfo,
    UserItemInfo,
    UserWallet,
} from 'api'
import { Perms } from 'shared/perms'
import { isServer } from 'solid-js/web'
import { LOCALE } from 'locale'
import {
    BROWSERS,
    cloneDefault,
    daysUntil,
    DEVICES,
    discountExpired,
    userHasPreferences,
} from 'shared'
import { native_theme_get, native_theme_set } from 'bridge'
import { EATERY_CARD_DEF, USER_PREFERENCES_DEF } from 'shared/defs'
import { Sector } from 'shared/landmark'
import { LOGIN_STATE_ID, setLoginStage } from 'pages/login/shared'
import { setForceShowLogin } from 'app'

export const BIRTHDAY_REMAINING_ANIM = 7

export type LoadingStateT = {
    active: number
    itterated: number
    started: boolean
    ended: boolean
}
export const [loadingState, setLoadingState] = createStore<LoadingStateT>({
    active: 0,
    itterated: 0,

    started: false,
    ended: false,
})

export type UserHoldingDiscount = {
    discount: Extract<UserItemInfo, { kind: 'discount_holding' }>
    eatery: EateryCard
}

export type SelfModel = {
    loading: boolean
    user: UserInfo | null
    perms: Perms
    impressions: UserImpression[]
    preferences: EateryStats | null
    saved_eateries: {
        last_updated: number
        belt_head: Gene
        map: Partial<Record<string, Gene>>
    }
    has_notif_perm: boolean | null
    is_bday_close: boolean
    holding_discounts: UserHoldingDiscount[]
}

const SELF_DEFAULT: SelfModel = {
    loading: true,
    user: null,
    perms: new Perms([]),
    impressions: [],
    preferences: cloneDefault(USER_PREFERENCES_DEF).eatery,
    saved_eateries: {
        last_updated: 0,
        belt_head: '',
        map: {},
    },
    has_notif_perm: null,
    is_bday_close: false,
    holding_discounts: [],
}

const [self, setSelf] = createStore<SelfModel>(cloneDefault(SELF_DEFAULT))

;(async () => {
    await fetch_user()
})()

async function fetch_user(withLoading = false) {
    setSelf('loading', withLoading)

    let res = await api_user_get({
        show_alert: false,
    })

    if (!res.ok())
        return setSelf({
            loading: false,
            user: null,
            perms: new Perms([]),
            impressions: [],
        })

    let user = res.body

    let preferences: SelfModel['preferences'] = null
    if (user.preferences && userHasPreferences(user.preferences.eatery)) {
        preferences = user.preferences!.eatery
    }

    if (umami) {
        umami.identify(user.gene, { id: user.gene, name: user.name })
    }

    // get_user_impressions()

    if (user.inventory.discounts_holding?.tail) {
        get_user_holding_discounts(user.inventory.discounts_holding.tail)
    }

    if (user.saved_eateries) {
        const head = user.saved_eateries.head

        get_user_saved_eateries(head)

        setSelf('saved_eateries', 'belt_head', head)
    }

    const bday = user.birth_date

    const is_bday_close = daysUntil(bday).days <= BIRTHDAY_REMAINING_ANIM

    // force login if main infos missing
    let w: LOGIN_STATE_ID[] = whatUserNeeds(user)
    if (w.length != 0) {
        setLoginStage(w)
        setForceShowLogin(true)
    } else {
        setForceShowLogin(false)
    }
    //

    setSelf({
        user,
        loading: false,
        preferences,
        perms: new Perms(user.agent?.admin_perms || []),
        is_bday_close,
    })

    setDB(USER_WELCOMED, 'true')
}

export function whatUserNeeds(user: UserInfo): LOGIN_STATE_ID[] {
    let loginStages: LOGIN_STATE_ID[] = []

    if (!user.home_location.latitude) {
        loginStages.push('homepick')
    }

    if (!user.name) {
        loginStages.push('name')
    }

    if (!user.preferences) {
        loginStages.push('preference')
    }

    return loginStages
}

export function update_user_notif_perm(hasPerm: SelfModel['has_notif_perm']) {
    setSelf('has_notif_perm', hasPerm)
}
export function update_user_agent(agent: AgentInfo) {
    setSelf('user', { agent })
}
export function update_user_inventory(inventory: UserInventoryInfo) {
    setSelf('user', { inventory })
}
export function update_user_info(info: Partial<UserInfo>) {
    setSelf('user', { ...info })
}
export function clear_user_notifs() {
    setSelf('user', 'notifications', false)
}
export function remove_session_from_user(
    sessionId: number,
    onCurrentLogout?: () => void
) {
    let current = self.user?.session_index == sessionId
    setSelf(
        produce(s => {
            s.user?.sessions.splice(sessionId, 1)
        })
    )
    if (current) {
        logout_user()
        onCurrentLogout && onCurrentLogout()
    }
}

export const user_has_username = () => !!self.user?.username
export const user_has_bday = () => !!self.user?.birth_date
export const user_has_home = () => !!self.user?.home_location.latitude

export const user_set_is_bday_close = () =>
    setSelf(
        'is_bday_close',
        self.user?.birth_date
            ? daysUntil(self.user?.birth_date).days <= BIRTHDAY_REMAINING_ANIM
            : false
    )

export const user_has_all_info = () =>
    user_has_username() && user_has_bday() && user_has_home()

export function set_user_home({
    latitude,
    longitude,
}: Omit<UserInfo['home_location'], 'sector'>) {
    setSelf('user', 'home_location', {
        latitude,
        longitude,
    })
}

// function get_user_impressions() {
//     api_user_impressions_list({ show_alert: false }).then(res => {
//         if (!res.ok()) return setSelf('impressions', [])

//         return setSelf('impressions', res.body)
//     })
// }

export function get_user_holding_discounts(pond_gene: Gene, onOk?: () => void) {
    if (!pond_gene) return

    api_user_inventory_get({ pond_gene }, { show_alert: false }).then(res => {
        if (!res.ok()) return

        let list = res.body.list

        const eateries_list = res.body.eateries

        list = list.filter(
            d => d.kind == 'discount_holding' && !discountExpired(d.expires_at)
        )

        const eateryMap = new Map<Gene, EateryCard>(
            eateries_list.map(e => [e.gene, e])
        )

        const holding_discounts = list
            .filter(
                (d): d is Extract<UserItemInfo, { kind: 'discount_holding' }> =>
                    d.kind === 'discount_holding'
            )
            .map(discount => ({
                discount,
                eatery: eateryMap.get(discount.eatery) || EATERY_CARD_DEF,
            }))

        setSelf('holding_discounts', holding_discounts)

        onOk?.()
    })
}
async function get_user_saved_eateries(belt: Gene) {
    if (!belt) return

    const res = await api_user_saved_raw_get({ belt }, { show_alert: false })

    const savedMap: SelfModel['saved_eateries']['map'] = {}

    if (res.ok()) {
        for (const gene of res.body.saved) {
            if (!gene) continue

            savedMap[gene] = belt
        }
    }

    setSelf(
        produce(s => {
            s.saved_eateries.map = savedMap
            s.saved_eateries.last_updated++
        })
    )
}

type AddOrRemoveT =
    | {
          kind: 'add'
          gene: Gene
          belt: Gene
          update?: boolean
      }
    | {
          kind: 'del'
          gene: Gene
          update?: boolean
      }

export function userAddOrRemoveFromSaved(props: AddOrRemoveT) {
    const update = props.update ?? true

    setSelf(
        produce(s => {
            if (!props.gene) return

            if (props.kind === 'del') {
                delete s.saved_eateries.map[props.gene]
            } else {
                s.saved_eateries.map[props.gene] = props.belt
            }

            if (update) {
                s.saved_eateries.last_updated++
            }
        })
    )
}

export function reRenderUserSaved() {
    setSelf(
        produce(s => {
            s.saved_eateries.last_updated++
        })
    )
}

export async function user_impression_add(imp: UserImpression) {
    setSelf(
        produce(s => {
            let id = s.impressions.findIndex(i => i.eatery == imp.eatery)

            if (id == -1) {
                s.impressions.push({ ...imp })
            } else {
                s.impressions[id]! = imp
            }
        })
    )
}
export async function user_impression_del(eatery: Gene) {
    setSelf(
        produce(s => {
            let id = s.impressions.findIndex(i => i.eatery == eatery)

            if (id != -1) {
                s.impressions.splice(id, 1)
            }
        })
    )
}

async function logout_user(): Promise<void> {
    setSelf({
        ...cloneDefault(SELF_DEFAULT),
        loading: false,
    })

    await api_user_logout_post({ show_alert: false })
}

// theme
const ALLOWED_THEMES = ['light', 'dark'] as const
type Theme = (typeof ALLOWED_THEMES)[number]

const [theme, setTheme] = createSignal<Theme>(defaultTheme())
export { theme }

export function app_set_theme(value: Theme) {
    setTheme(value)
    native_theme_set(value)
}

function defaultTheme(): Theme {
    if (isServer) return 'dark'

    let value = localStorage.getItem(USER_THEME) as Theme
    if (value && ALLOWED_THEMES.includes(value as Theme)) return value

    if (matchMedia && matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark'
    }
    return 'light'
}

// alert

type AlertModel = {
    type: 'info' | 'error' | 'success'
    id: string
    subject: string
    // content?: string
    timeout: number
}

type AlertState = {
    alerts: AlertModel[]
}

const [alert_state, setAlertState] = createStore<AlertState>({
    alerts: [],
})

export function addAlert(props: Omit<AlertModel, 'id'>) {
    if (alert_state.alerts.length >= 2) return

    const id = createUniqueId()
    setAlertState(
        produce(s => {
            s.alerts.unshift({ ...props, id })
        })
    )
}

export function delAlert(id: string) {
    const index = alert_state.alerts.findIndex(a => a.id === id)

    if (index === -1) return

    setAlertState(
        produce(s => {
            s.alerts.splice(index, 1)
        })
    )
}
export function alert_code(code: SeedError) {
    let msg = LOCALE.seed_error(code)

    addAlert({
        type: 'error',
        timeout: 3,
        subject: msg,
        // content: '',
    })
}
// alert

createRoot(() => {
    onMount(() => {
        native_theme_get().then(r => {
            if (r && r != theme()) setTheme(r)
        })
    })

    createEffect(() => {
        localStorage.setItem(USER_THEME, theme())
        document.documentElement.setAttribute('data-theme', theme())
    })
})

// forgot name
type ForgotNameState = {
    show: boolean
    name: string
    gender: UserGender
    loading: boolean
    status: 'green' | 'red' | null
}
const [forgotNameS, setForgotNameS] = createStore<ForgotNameState>({
    show: false,
    name: '',
    gender: 'male',
    loading: false,
    status: null,
})
export let forgotNameRef: HTMLElement
export const setforgotNameRef = (e: HTMLElement) => (forgotNameRef = e)

// forgot bday
type ForgotBdayState = {
    timestamp: {
        day: number
        month: number
        year: number
    }
    status: 'green' | 'red' | null
    show: boolean
    loading: boolean
}
const [forgotBdayS, setForgotBdayS] = createStore<ForgotBdayState>({
    show: false,
    loading: false,
    timestamp: {
        day: -1,
        month: -1,
        year: -1,
    },
    status: null,
})
export let forgotBirthdayRef: HTMLElement
export const setforgotBdayRef = (e: HTMLElement) => (forgotBirthdayRef = e)

// popup
type popupTypes = 'success' | 'info' | 'error' | 'warning'
type PopupProps = {
    show: boolean

    type: popupTypes
    Icon: () => JSX.Element | null
    title: string
    content: string

    timer: number

    onSubmit: () => void
    onReject: () => void

    class?: string
}
export const DEFAULT_POPUP: PopupProps = {
    show: false,
    type: 'info',
    Icon: () => null,
    title: '',
    timer: 0,
    content: '',
    onReject: () => {},
    onSubmit: () => {},

    class: '',
}

const [popupS, setPopupS] = createStore<PopupProps>({ ...DEFAULT_POPUP })
// popup

// home loc
type HOMELOC_COORDS = {
    lat: number
    lng: number
}

type HOMELOC = {
    show: boolean
    stage: 'sector' | 'map'
    sector: Sector | null
    coords: HOMELOC_COORDS | null
    onSubmit(lat: number, lng: number): void
    onReject(): void
    customZoom: number | null
    goToUser: boolean
}

export const DEFAULT_HOME_LOC_STATE: HOMELOC = {
    show: false,
    stage: 'sector',
    sector: null,
    coords: null,
    onSubmit: () => {},
    onReject: () => {},
    customZoom: null,
    goToUser: false,
}

const [locPickerState, setLocPickerState] = createStore<HOMELOC>(
    cloneDefault(DEFAULT_HOME_LOC_STATE)
)

type INIT_HOME_PICKER = {
    coords: HOMELOC['coords']
    sector?: HOMELOC['sector']
    onSubmit?: HOMELOC['onSubmit']
    onReject?: HOMELOC['onReject']
    stage?: HOMELOC['stage']
    customZoom?: HOMELOC['customZoom']
    goToUser?: HOMELOC['goToUser']
}

export const initHomePicker = ({
    coords,
    sector = null,
    onSubmit = () => {},
    onReject = () => {},
    stage = coords ? 'map' : 'sector',
    customZoom = null,
    goToUser = false,
}: INIT_HOME_PICKER) => {
    setLocPickerState({
        show: true,
        coords,
        sector,
        onSubmit,
        onReject,
        stage,
        customZoom,
        goToUser,
    })
}
export const clearHomePickerState = () =>
    setLocPickerState(cloneDefault(DEFAULT_HOME_LOC_STATE))

export let homePickerMap: maplibregl.Map | undefined
export const setHomePickerMap = (e: maplibregl.Map) => (homePickerMap = e)
// home loc

// share us
type ShareUsState = {
    show: boolean
    ended: boolean
}
const [shareUsS, setShareUsS] = createStore<ShareUsState>({
    show: false,
    ended: false,
})
export let shareUsRef: HTMLElement | null = null
export const setshareUsRef = (e: HTMLElement) => (shareUsRef = e)
// share us

// account info

export type BROWSERS_TYPES = (typeof BROWSERS)[number]

type BROWSER_PERSIAN = {
    [k in BROWSERS_TYPES]: string
}
const BROWSER_PERSIAN: BROWSER_PERSIAN = {
    unknown: 'مروگر',
    chrome: 'کروم',
    edge: 'edge',
    opera: 'اوپرا',
    safari: 'سافاری',
    firefox: 'فایرفاکس',
}

export type DEVICES_TYPES = (typeof DEVICES)[number]

export type DEVICE_PERSIAN = {
    [k in DEVICES_TYPES]: string
}
const DEVICE_PERSIAN: DEVICE_PERSIAN = {
    unknown: 'دستگاه',
    desktop: 'PC',
    mobile: 'موبایل',
    embeded: 'embeded',
}
type ACC_INFO_T = {
    loadingImg: boolean
    imgLoaded: boolean
    editing: boolean
    loading: boolean
}
export const ACC_INFO_S_DEF: ACC_INFO_T = {
    imgLoaded: false,
    loadingImg: false,
    editing: false,
    loading: false,
}
const [accInfoS, setAccInfoS] = createStore<ACC_INFO_T>(
    cloneDefault(ACC_INFO_S_DEF)
)
// account info

export {
    self,
    setSelf,
    fetch_user,
    logout_user,
    alert_state,
    setAlertState,
    type AlertModel,
    forgotNameS,
    setForgotNameS,
    forgotBdayS,
    setForgotBdayS,
    popupS,
    setPopupS,
    locPickerState,
    setLocPickerState,
    type HOMELOC,
    shareUsS,
    setShareUsS,
    accInfoS,
    setAccInfoS,
}

export function self_wallet_patch(wallet: Partial<UserWallet>) {
    setSelf(
        produce(s => {
            if (!s.user) return

            for (let [k, v] of Object.entries(wallet)) {
                if (typeof v != 'number') continue
                s.user.wallet[k as keyof UserWallet] = v
            }
        })
    )
}
